import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@freelancer-os/ui/src/components/ui/sheet';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Input } from '@freelancer-os/ui/src/components/ui/input';
import { Label } from '@freelancer-os/ui/src/components/ui/label';
import { Textarea } from '@freelancer-os/ui/src/components/ui/textarea';
import { cn } from '@freelancer-os/ui/src/lib/utils';
import { Banknote, Building2, ShoppingBag, MoreHorizontal, Check } from 'lucide-react';
import type { Invoice } from '@freelancer-os/shared-types';
import { formatVnd, parseVndInput } from '../../../lib/formatVnd';
import { useClients } from '../../../hooks/useClients';
import { useRecordPayment } from '../../../hooks/usePayments';

type Source = 'CASH' | 'MANUAL_BANK' | 'ECOMMERCE' | 'OTHER';

const SOURCE_OPTIONS: Array<{
  value: Source;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}> = [
  { value: 'CASH', icon: Banknote },
  { value: 'MANUAL_BANK', icon: Building2 },
  { value: 'ECOMMERCE', icon: ShoppingBag },
  { value: 'OTHER', icon: MoreHorizontal },
];

type Mode =
  | { kind: 'invoice'; invoice: Pick<Invoice, 'id' | 'invoiceCode'>; defaultAmount: number }
  | { kind: 'adhoc' };

interface RecordPaymentDrawerProps {
  open: boolean;
  mode: Mode;
  onOpenChange: (open: boolean) => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RecordPaymentDrawer({ open, mode, onOpenChange }: RecordPaymentDrawerProps) {
  const { t } = useTranslation('payments');
  const formKey = open ? (mode.kind === 'invoice' ? `inv-${mode.invoice.id}` : 'adhoc') : 'closed';
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('drawer.title')}</SheetTitle>
          <SheetDescription>
            {mode.kind === 'invoice'
              ? t('drawer.subtitle.invoice', { code: mode.invoice.invoiceCode })
              : t('drawer.subtitle.adhoc')}
          </SheetDescription>
        </SheetHeader>
        <RecordForm key={formKey} mode={mode} onOpenChange={onOpenChange} />
      </SheetContent>
    </Sheet>
  );
}

function RecordForm({
  mode,
  onOpenChange,
}: {
  mode: Mode;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('payments');
  const defaultAmount = mode.kind === 'invoice' ? mode.defaultAmount : 0;

  const [amount, setAmount] = useState<number>(defaultAmount);
  const [amountInput, setAmountInput] = useState<string>(
    defaultAmount > 0 ? formatVnd(defaultAmount) : '',
  );
  const [date, setDate] = useState<string>(todayIso());
  const [source, setSource] = useState<Source>('CASH');
  const [content, setContent] = useState<string>('');
  const [clientId, setClientId] = useState<string | null>(null);

  const { data: clients } = useClients();
  const recordMutation = useRecordPayment();

  const isPending = recordMutation.isPending;
  const canSubmit = amount > 0 && !!date && !isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const payload =
      mode.kind === 'invoice'
        ? {
            source,
            amount,
            transactionDate: new Date(`${date}T00:00:00.000Z`).toISOString(),
            content: content.trim(),
            invoiceId: mode.invoice.id,
          }
        : {
            source,
            amount,
            transactionDate: new Date(`${date}T00:00:00.000Z`).toISOString(),
            content: content.trim(),
            clientId: clientId || null,
          };
    recordMutation.mutate(payload, {
      onSuccess: (result) => {
        const status = result.invoiceStatus;
        if (status === 'PAID') toast.success(t('toast.paid'));
        else if (status === 'PARTIAL') toast.success(t('toast.partial'));
        else toast.success(t('toast.adhoc'));
        onOpenChange(false);
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 py-4">
      <div className="space-y-2">
        <Label htmlFor="pay-amount">{t('drawer.field.amount')}</Label>
        <Input
          id="pay-amount"
          inputMode="numeric"
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          onBlur={() => {
            const n = parseVndInput(amountInput);
            setAmount(n);
            setAmountInput(n > 0 ? formatVnd(n) : '');
          }}
          placeholder={t('drawer.field.amountPlaceholder')}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="pay-date">{t('drawer.field.date')}</Label>
        <Input
          id="pay-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label>{t('drawer.field.source')}</Label>
        <div className="flex flex-wrap gap-2">
          {SOURCE_OPTIONS.map((opt) => {
            const isSelected = source === opt.value;
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSource(opt.value)}
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-2 border text-sm font-medium transition-all rounded-md',
                  isSelected
                    ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500 dark:bg-brand-500/20 dark:text-brand-400'
                    : 'border-surface-200 text-surface-600 hover:border-brand-300 hover:bg-brand-50/50',
                )}
              >
                <Icon
                  className={cn(
                    'w-4 h-4',
                    isSelected ? 'text-brand-600 dark:text-brand-400' : 'text-surface-400',
                  )}
                  strokeWidth={1.8}
                />
                {t(`drawer.source.${opt.value}`)}
                {isSelected && (
                  <Check
                    className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400"
                    strokeWidth={2.5}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pay-content">{t('drawer.field.content')}</Label>
        <Textarea
          id="pay-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t(`drawer.field.contentPlaceholder.${source}`)}
          rows={2}
        />
      </div>

      {mode.kind === 'adhoc' && (
        <div className="space-y-2">
          <Label htmlFor="pay-client">{t('drawer.field.client')}</Label>
          <select
            id="pay-client"
            value={clientId ?? ''}
            onChange={(e) => setClientId(e.target.value || null)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
          >
            <option value="">{t('drawer.field.noClient')}</option>
            {(clients ?? []).map((cl) => (
              <option key={cl.id} value={cl.id}>
                {cl.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <SheetFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          {t('drawer.cancel')}
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {isPending ? t('drawer.saving') : t('drawer.save')}
        </Button>
      </SheetFooter>
    </form>
  );
}
